/**
 * GettingStartedCard Component
 *
 * Expandable onboarding panel shown on the Dashboard when the user clicks
 * "Get Started". Presents key features as independent cards (not sequential
 * steps) since recipes and jobs are independent of each other.
 *
 * Toggled via the `expanded` prop controlled by the parent.
 */

import React from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Grid,
    Collapse,
    alpha,
    useTheme,
} from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import WorkIcon from '@mui/icons-material/Work';
import FolderIcon from '@mui/icons-material/Folder';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ForumIcon from '@mui/icons-material/Forum';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useNavigate } from 'react-router-dom';

interface FeatureItem {
    icon: React.ReactElement;
    title: string;
    description: string;
    actionLabel: string;
    actionPath: string;
}

const FEATURES: FeatureItem[] = [
    {
        icon: <WorkIcon />,
        title: 'Create a Job',
        description: 'Choose from 6 powerful job modes for audio generation. Fully customizable, fully controllable. Manage jobs in real time.',
        actionLabel: 'Create a Job',
        actionPath: '/create',
    },
    {
        icon: <MenuBookIcon />,
        title: 'Recipes',
        description: 'Save your favorite voice and style settings as reusable recipes. Create them from scratch or save from a completed job.',
        actionLabel: 'My Recipes',
        actionPath: '/recipes',
    },
    {
        icon: <StorefrontIcon />,
        title: 'Market',
        description: 'Discover community-shared recipes with different voices, styles, and configurations ready to use.',
        actionLabel: 'Browse Market',
        actionPath: '/market',
    },
    {
        icon: <FolderIcon />,
        title: 'Projects',
        description: 'Organize related jobs into projects to keep your work structured and easy to find.',
        actionLabel: 'View Projects',
        actionPath: '/projects',
    },
    {
        icon: <ForumIcon />,
        title: 'Community Forum',
        description: 'Ask questions, share tips, and connect with other users in the community forum.',
        actionLabel: 'Visit Forum',
        actionPath: '/forum',
    },
    {
        icon: <HelpOutlineIcon />,
        title: 'Support',
        description: 'Need help? Browse FAQs, troubleshooting guides, and reach out to our team anytime.',
        actionLabel: 'Get Help',
        actionPath: '/support',
    },
];

export interface GettingStartedCardProps {
    /** Whether the card is expanded/visible */
    expanded: boolean;
    /** Callback to collapse the card */
    onClose: () => void;
}

export const GettingStartedCard: React.FC<GettingStartedCardProps> = ({ expanded, onClose }) => {
    const navigate = useNavigate();
    const theme = useTheme();

    return (
        <Collapse in={expanded} unmountOnExit sx={{ width: '100%', maxWidth: 720 }}>
            <Card
                data-testid="getting-started-card"
                sx={{
                    mb: 3,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.04)} 0%, ${alpha(theme.palette.secondary.main, 0.04)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                }}
            >
                <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
                    <Typography variant="h6" fontWeight={600} sx={{ textAlign: 'center', mb: 1 }}>
                        Welcome to VoiceRecipe!
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', mb: 3, maxWidth: 480, mx: 'auto' }}>
                        Turn any text into natural-sounding audio! Explore the features below and dive right in.
                    </Typography>

                    {/* Feature cards grid */}
                    <Grid container spacing={2}>
                        {FEATURES.map((feature) => (
                            <Grid item xs={12} sm={6} key={feature.title}>
                                <Box
                                    sx={{
                                        p: 2,
                                        borderRadius: 1.5,
                                        border: `1px solid ${theme.palette.divider}`,
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: theme.shadows[4],
                                        },
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <Box
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                color: 'primary.main',
                                                '& .MuiSvgIcon-root': { fontSize: 18 },
                                            }}
                                        >
                                            {feature.icon}
                                        </Box>
                                        <Typography variant="subtitle2" fontWeight={600}>
                                            {feature.title}
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, flex: 1 }}>
                                        {feature.description}
                                    </Typography>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => navigate(feature.actionPath)}
                                        data-testid={`getting-started-${feature.title.toLowerCase().replace(/\s+/g, '-')}`}
                                        sx={{ alignSelf: 'flex-start' }}
                                    >
                                        {feature.actionLabel}
                                    </Button>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>

                    {/* Close button */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                        <Button
                            variant="outlined"
                            onClick={onClose}
                            data-testid="getting-started-close"
                        >
                            Got it!
                        </Button>
                    </Box>
                </CardContent>
            </Card>
        </Collapse>
    );
};
